import { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import "./commentsSection.scss";
import { selectCurrentToken } from "../../features/auth/authSlice";
import {
  useAddCommentMutation,
  useDeleteCommentMutation,
  useGetCommentsQuery,
} from "../../features/api/commentsApiSlice";
import LoadingSpinner from "../loader/Loader";

// Recursive component to render comments and replies
const Comment = ({ comment, videoId, onReply }) => {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const token = useSelector(selectCurrentToken);
  const [deleteComment] = useDeleteCommentMutation();
  const userId = useSelector((state) => state.auth?.user?._id);

  const handleDelete = async () => {
    try {
      await deleteComment(comment._id).unwrap();
      toast.success("Comment deleted successfully!");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to delete comment.");
    }
  };

  return (
    <div className="comment-item">
      <div className="comment-header">
        <span className="comment-author">
          {comment.user.firstName} {comment.user.lastName}
        </span>
        <span className="comment-date">
          {new Date(comment.createdAt).toLocaleString()}
        </span>
      </div>
      <div className="comment-text">{comment.text}</div>
      <div className="comment-actions">
        {token && (
          <button onClick={() => setShowReplyForm(!showReplyForm)}>
            Reply
          </button>
        )}
        {token && comment.user._id === userId && (
          <button onClick={handleDelete}>Delete</button>
        )}
      </div>

      {showReplyForm && (
        <CommentForm
          videoId={videoId}
          parentCommentId={comment._id}
          onCommentAdded={() => setShowReplyForm(false)}
        />
      )}

      <div className="comment-replies">
        {comment.replies?.map((reply) => (
          <Comment
            key={reply._id}
            comment={reply}
            videoId={videoId}
            onReply={onReply}
          />
        ))}
      </div>
    </div>
  );
};

// Form component for adding comments and replies
const CommentForm = ({ videoId, parentCommentId = null, onCommentAdded }) => {
  const [text, setText] = useState("");
  const [addComment, { isLoading }] = useAddCommentMutation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;

    try {
      await addComment({ videoId, text, parentCommentId }).unwrap();
      setText("");
      toast.success("Comment added successfully!");
      if (onCommentAdded) onCommentAdded();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to add comment.");
    }
  };

  return (
    <form className="comment-form" onSubmit={handleSubmit}>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Add a comment..."
        rows={3}
        required
      />
      <button type="submit" disabled={isLoading}>
        {isLoading ? "Submitting..." : "Submit"}
      </button>
    </form>
  );
};

// Main component to fetch and display all comments
const CommentsSection = ({ videoId }) => {
  const { data: comments, isLoading, error } = useGetCommentsQuery(videoId);
  const token = useSelector(selectCurrentToken);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <p>Error loading comments.</p>;

  return (
    <div className="comments-section">
      <h3>Comments ({comments?.length || 0})</h3>
      {token && <CommentForm videoId={videoId} />}
      <div className="comments-list">
        {comments?.map((comment) => (
          <Comment key={comment._id} comment={comment} videoId={videoId} />
        ))}
      </div>
    </div>
  );
};

export default CommentsSection;
